import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import {
  HR_APPLICATION_STAGES,
  HR_EMPLOYEE_TYPES,
  HR_OFFER_STATUSES,
  HR_REQUISITION_STATUSES,
  HrApplication,
  HrCandidate,
  HrInterview,
  HrOffer,
  HrOfferConversionRequest,
  HrRecruitmentDashboard,
  HrRequisition
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

interface SelectOption<T = string | number | boolean> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-hr-recruitment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-recruitment.component.html',
  styleUrls: ['./hr-recruitment.component.scss']
})
export class HrRecruitmentComponent implements OnInit {
  activeListTab = 'requisitions';
  activeDialog?: 'requisition' | 'candidate' | 'application' | 'interview' | 'offer' | 'conversion';
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  readonly canManage = this.permissionService.hasPermission(app_permissions.hr.recruitment.manage);
  readonly canManageOffers = this.permissionService.hasPermission(app_permissions.hr.recruitment.offersManage);

  readonly statusOptions = HR_REQUISITION_STATUSES.map(value => ({ label: value, value }));
  readonly stageOptions = HR_APPLICATION_STAGES.map(value => ({ label: value, value }));
  readonly offerStatusOptions = HR_OFFER_STATUSES.map(value => ({ label: value, value }));
  workerCategoryOptions: SelectOption<string>[] = [];
  readonly employeeTypeOptions = HR_EMPLOYEE_TYPES.map(value => ({ label: value, value }));
  readonly yesNoOptions: SelectOption<boolean>[] = [
    { label: 'Start onboarding', value: true },
    { label: 'Skip onboarding', value: false }
  ];

  dashboard?: HrRecruitmentDashboard;
  requisitions: HrRequisition[] = [];
  candidates: HrCandidate[] = [];
  applications: HrApplication[] = [];
  offers: HrOffer[] = [];

  requisitionForm: HrRequisition = { requisitionStatus: 'DRAFT', priority: 'NORMAL', openings: 1 };
  candidateForm: HrCandidate = { candidateStatus: 'ACTIVE' };
  applicationForm: HrApplication = { stage: 'APPLIED', complianceStatus: 'PENDING' };
  interviewForm: HrInterview = { interviewType: 'SCREENING', interviewStatus: 'SCHEDULED' };
  offerForm: HrOffer = { offerStatus: 'DRAFT', currencyCode: 'SAR' };
  conversionForm: HrOfferConversionRequest = { startOnboarding: true };
  selectedOfferId?: number;
  candidateSearch = '';
  selectedStage?: string;

  ngOnInit(): void {
    this.loadWorkerCategories();
    this.loadAll();
  }

  private loadWorkerCategories(): void {
    this.hrService.workerCategories().subscribe(response => {
      this.workerCategoryOptions = (response.data ?? []).map(item => ({
        label: item.categoryName || item.categoryCode || '', value: item.categoryCode || ''
      }));
    });
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Open Requisitions', value: this.dashboard?.openRequisitions || 0, tone: 'info' },
      { label: 'Active Candidates', value: this.dashboard?.activeCandidates || 0, tone: 'success' },
      { label: 'Applications', value: this.dashboard?.activeApplications || 0, tone: 'info' },
      { label: 'Scheduled Interviews', value: this.dashboard?.scheduledInterviews || 0, tone: 'warn' },
      { label: 'Open Offers', value: this.dashboard?.openOffers || 0, tone: 'success' }
    ];
  }

  get requisitionOptions(): SelectOption<number>[] {
    return this.requisitions.map(item => ({
      label: `${item.requisitionCode || item.requisitionId} - ${item.requisitionTitle}`,
      value: item.requisitionId!
    }));
  }

  get candidateOptions(): SelectOption<number>[] {
    return this.candidates.map(item => ({
      label: `${item.candidateCode || item.candidateId} - ${item.displayName}`,
      value: item.candidateId!
    }));
  }

  get applicationOptions(): SelectOption<number>[] {
    return this.applications.map(item => ({
      label: `${item.applicationCode || item.applicationId} - ${item.candidateName || item.candidateId}`,
      value: item.applicationId!
    }));
  }

  get offerOptions(): SelectOption<number>[] {
    return this.offers.map(item => ({
      label: `${item.offerCode || item.offerId} - ${item.candidateName || item.candidateId}`,
      value: item.offerId!
    }));
  }

  loadAll(): void {
    forkJoin({
      dashboard: this.hrService.recruitmentDashboard(),
      requisitions: this.hrService.recruitmentRequisitions(),
      candidates: this.hrService.recruitmentCandidates(undefined, this.candidateSearch || undefined),
      applications: this.hrService.recruitmentApplications(undefined, this.selectedStage),
      offers: this.hrService.recruitmentOffers()
    }).subscribe({
      next: result => {
        this.dashboard = result.dashboard.data;
        this.requisitions = result.requisitions.data || [];
        this.candidates = result.candidates.data || [];
        this.applications = result.applications.data || [];
        this.offers = result.offers.data || [];
      },
      error: () => this.toast('error', 'Unable to load recruitment workspace.')
    });
  }

  saveRequisition(): void {
    if (!this.canManage) return;
    this.hrService.saveRecruitmentRequisition(this.requisitionForm).subscribe({
      next: () => {
        this.requisitionForm = { requisitionStatus: 'DRAFT', priority: 'NORMAL', openings: 1 };
        this.activeDialog = undefined;
        this.toast('success', 'Requisition saved.');
        this.loadAll();
      },
      error: () => this.toast('error', 'Unable to save requisition.')
    });
  }

  approveRequisition(requisition: HrRequisition): void {
    if (!this.canManage || !requisition.requisitionId) return;
    this.hrService.decideRecruitmentRequisition(requisition.requisitionId, { status: 'APPROVED' }).subscribe({
      next: () => {
        this.toast('success', 'Requisition opened.');
        this.loadAll();
      },
      error: () => this.toast('error', 'Unable to approve requisition.')
    });
  }

  saveCandidate(): void {
    if (!this.canManage) return;
    this.hrService.saveRecruitmentCandidate(this.candidateForm).subscribe({
      next: () => {
        this.candidateForm = { candidateStatus: 'ACTIVE' };
        this.activeDialog = undefined;
        this.toast('success', 'Candidate saved.');
        this.loadAll();
      },
      error: () => this.toast('error', 'Unable to save candidate.')
    });
  }

  createApplication(): void {
    if (!this.canManage) return;
    this.hrService.createRecruitmentApplication(this.applicationForm).subscribe({
      next: () => {
        this.applicationForm = { stage: 'APPLIED', complianceStatus: 'PENDING' };
        this.activeDialog = undefined;
        this.toast('success', 'Application created.');
        this.loadAll();
      },
      error: () => this.toast('error', 'Unable to create application.')
    });
  }

  transitionApplication(application: HrApplication, stage: string): void {
    if (!this.canManage || !application.applicationId) return;
    this.hrService.transitionRecruitmentApplication(application.applicationId, { stage }).subscribe({
      next: () => {
        this.toast('success', 'Application stage updated.');
        this.loadAll();
      },
      error: () => this.toast('error', 'Unable to update application stage.')
    });
  }

  saveInterview(): void {
    if (!this.canManage) return;
    this.hrService.saveRecruitmentInterview(this.interviewForm).subscribe({
      next: () => {
        this.interviewForm = { interviewType: 'SCREENING', interviewStatus: 'SCHEDULED' };
        this.activeDialog = undefined;
        this.toast('success', 'Interview saved.');
        this.loadAll();
      },
      error: () => this.toast('error', 'Unable to save interview.')
    });
  }

  saveOffer(): void {
    if (!this.canManageOffers) return;
    this.hrService.saveRecruitmentOffer(this.offerForm).subscribe({
      next: () => {
        this.offerForm = { offerStatus: 'DRAFT', currencyCode: 'SAR' };
        this.activeDialog = undefined;
        this.toast('success', 'Offer saved.');
        this.loadAll();
      },
      error: () => this.toast('error', 'Unable to save offer.')
    });
  }

  decideOffer(offer: HrOffer, decision: string): void {
    if (!this.canManageOffers || !offer.offerId) return;
    this.hrService.decideRecruitmentOffer(offer.offerId, { decision }).subscribe({
      next: () => {
        this.toast('success', 'Offer decision saved.');
        this.loadAll();
      },
      error: () => this.toast('error', 'Unable to save offer decision.')
    });
  }

  openActionDialog(dialog: NonNullable<HrRecruitmentComponent['activeDialog']>): void {
    this.activeDialog = dialog;
  }

  actionDialogVisible(dialog: NonNullable<HrRecruitmentComponent['activeDialog']>): boolean {
    return this.activeDialog === dialog;
  }

  setActionDialogVisible(dialog: NonNullable<HrRecruitmentComponent['activeDialog']>, visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  convertOffer(): void {
    if (!this.canManageOffers || !this.conversionForm.employeeNumber) return;
    const offerId = this.selectedOfferId;
    if (!offerId) return;
    this.hrService.convertRecruitmentOffer(offerId, this.conversionForm).subscribe({
      next: () => {
        this.conversionForm = { startOnboarding: true };
        this.selectedOfferId = undefined;
        this.activeDialog = undefined;
        this.toast('success', 'Offer converted to employee.');
        this.loadAll();
      },
      error: () => this.toast('error', 'Unable to convert offer.')
    });
  }

  severity(status?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' {
    if (['OPEN', 'ACTIVE', 'APPROVED', 'ACCEPTED', 'CONVERTED', 'HIRED'].includes(status || '')) return 'success';
    if (['REJECTED', 'DECLINED', 'CANCELLED'].includes(status || '')) return 'danger';
    if (['DRAFT', 'PENDING_APPROVAL', 'PENDING'].includes(status || '')) return 'warn';
    return 'info';
  }

  private toast(severity: 'success' | 'error', detail: string): void {
    this.messageService.add({ severity, summary: 'HR Recruitment', detail });
  }
}
